<script setup>
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import InternalChatAudioControls from './InternalChatAudioControls.vue';
defineProps({
  disabled: { type: Boolean, default: false },
  audioVisible: { type: Boolean, default: false },
  audioBusy: { type: Boolean, default: false },
  audioRecording: { type: Object, default: null },
  canSend: { type: Boolean, default: false },
  canMention: { type: Boolean, default: false },
  mentionMenuOpen: { type: Boolean, default: false },
  isThread: { type: Boolean, default: false },
  alsoSendInChannel: { type: Boolean, default: false },
  audioError: { type: String, default: '' },
  error: { type: String, default: '' },
  validationError: { type: String, default: '' },
});
const emit = defineEmits([
  'attach',
  'record',
  'discardAudio',
  'audioPlayStop',
  'mention',
  'send',
  'update:alsoSendInChannel',
]);
const { t } = useI18n();
</script>

<template>
  <div
    class="flex flex-wrap items-center gap-2 border-t border-n-weak p-3"
    data-testid="internal-chat-composer-controls"
  >
    <Button
      type="button"
      variant="faded"
      color="slate"
      size="sm"
      icon="i-lucide-paperclip"
      :aria-label="t('INTERNAL_CHAT.ATTACH_FILES')"
      :disabled="disabled"
      @click="emit('attach')"
    />
    <InternalChatAudioControls
      :active="audioVisible"
      :disabled="disabled"
      :recording="audioRecording"
      @record="emit('record')"
      @discard="emit('discardAudio')"
      @play-stop="emit('audioPlayStop')"
    />
    <Button
      v-if="canMention"
      type="button"
      variant="faded"
      color="slate"
      size="sm"
      icon="i-lucide-at-sign"
      :aria-label="t('INTERNAL_CHAT.MENTION')"
      :aria-expanded="mentionMenuOpen"
      :disabled="disabled || audioVisible"
      @mousedown.prevent
      @click="emit('mention')"
    />
    <Button
      type="button"
      size="sm"
      class="ms-auto shrink-0"
      :label="t('INTERNAL_CHAT.SEND')"
      :aria-label="t('INTERNAL_CHAT.SEND')"
      :disabled="disabled || audioBusy || !canSend"
      data-testid="internal-chat-send"
      @click="emit('send')"
    />
  </div>
  <label
    v-if="isThread"
    class="mb-0 flex items-center gap-2 border-t border-n-weak px-3 py-2 text-xs text-n-slate-11"
  >
    <input
      :checked="alsoSendInChannel"
      type="checkbox"
      :disabled="disabled"
      @change="emit('update:alsoSendInChannel', $event.target.checked)"
    />
    {{ t('INTERNAL_CHAT.ALSO_SEND_IN_CHANNEL') }}
  </label>
  <p
    v-if="audioRecording?.playbackError"
    class="mb-0 px-3 pb-3 text-xs text-n-ruby-11"
    role="alert"
  >
    {{ t('CONVERSATION.AUDIO_PLAYER.ERROR') }}
  </p>
  <p
    v-if="audioError"
    class="mb-0 px-3 pb-3 text-xs text-n-ruby-11"
    role="alert"
  >
    {{ t(audioError) }}
  </p>
  <p
    v-if="validationError"
    class="mb-0 px-3 pb-3 text-xs text-n-ruby-11"
    role="alert"
  >
    {{ validationError }}
  </p>
  <div v-if="error" class="flex items-center justify-between gap-3 px-3 pb-3">
    <p class="mb-0 text-xs text-n-ruby-11" role="alert">{{ error }}</p>
    <Button
      type="button"
      variant="ghost"
      color="slate"
      size="xs"
      class="shrink-0"
      :label="t('INTERNAL_CHAT.RETRY')"
      :disabled="disabled || audioBusy || !canSend"
      @click="emit('send')"
    />
  </div>
</template>
